import './AdjustmentGridView.scss';
import { useEffect } from 'react';
import { GridColumnProps } from '@progress/kendo-react-grid';
import { TableSelectableMode } from '@progress/kendo-react-data-tools';
import { Slide } from "@progress/kendo-react-animation";
import { DisputeButton, GridView } from 'components';
import { AdjustmentDetail } from 'types';
import { formatMoney } from 'functions';

export type AdjustmentGridViewProps = {
    adjustments: AdjustmentDetail[] , 
    selectedDetails?: AdjustmentDetail[] | null | undefined,
    setSelectedDetails?: (value: any[]) => void,
    setActiveSelectedDetail?: (value: AdjustmentDetail | undefined) => void,
    setExpanded?: (value: boolean) => void,
    selectMode?: TableSelectableMode,
    dataItemKey: string,
    gridColumns: GridColumnProps[],
    title?: string,
    isReadOnly?: boolean,
    showTotal?: boolean,
    disabled?: boolean,
    context?: string,
    expandOnMultipleSelection?: boolean,
    hideMultiSelectDisputeButton?: boolean,
    selectionGroupField?: string,
    selectionGroupTitle?: string
};

export function AdjustmentGridView({ adjustments, selectedDetails, setSelectedDetails,
    setExpanded, title = "", gridColumns, selectMode,
    dataItemKey, isReadOnly = false, showTotal = false, disabled, context,
    setActiveSelectedDetail, expandOnMultipleSelection = false,
    hideMultiSelectDisputeButton = false, selectionGroupField }: AdjustmentGridViewProps) {
    
    useEffect(() => {
        const hasSelection = selectedDetails && (
            selectedDetails.length === 1 || (expandOnMultipleSelection && selectedDetails.length > 1)
        );
        setExpanded && setExpanded(!!hasSelection);
    }, [expandOnMultipleSelection, selectedDetails, setExpanded]);
    
    const onSelectedItemsChange = (event: any) => {
        setSelectedDetails && setSelectedDetails(event.selectedItems);

        if (setActiveSelectedDetail) {
            const changedItemIsSelected = event.changedItem && event.selectedItems.some((item: AdjustmentDetail) => item[dataItemKey as keyof AdjustmentDetail] === event.changedItem[dataItemKey]);
            setActiveSelectedDetail(changedItemIsSelected
                ? event.changedItem
                : event.selectedItems[0]);
        }
    };

    const adjustmentColumnIndex = gridColumns.findIndex(col => col.field === 'adjustmentAmount');   
    const totalValue = adjustments.reduce((sum, item) => sum + item.adjustmentAmount || 0, 0);
    const getTotalAdjustments = formatMoney(totalValue);
    useEffect(() => {
        const root = document.querySelector('.adjustments-gridview') as HTMLElement;
        if (root) {
            root.style.setProperty('--grid-columns', gridColumns.length.toString());
            root.style.setProperty('--adjustment-column', (adjustmentColumnIndex + 1).toString());
        }
    }, [gridColumns, adjustmentColumnIndex]);

    return (
        <div className="adjustments-gridview">
            <div className="adjustments-gridview-heading">
                <div className="adjustments-gridview-heading-title">
                    <span>{title}</span> 
                    {isReadOnly === false && <span className='adjustments-gridview-heading-title-description'>Select a line to view details </span>}
                </div>
                <div className="adjustments-gridview-heading-dispute-button">
                    {(
                        isReadOnly === false && 
                        selectedDetails && 
                        selectedDetails?.length > 1 &&
                        !hideMultiSelectDisputeButton) &&
                        <Slide
                            appear={(selectedDetails && selectedDetails?.length > 1) ? true: false}
                            className="adjustments-gridview-heading-dispute-button-slider"
                            direction="left"
                        >
                            <DisputeButton 
                                selectedDetails={selectedDetails ?? []} 
                                disabled={disabled}
                                context={context}
                            />
                        </Slide>
                    }
                </div>
            </div>
            <GridView
                className="adjustments-gridview-grid"
                cellClassName="adjustments-gridview-grid-cell"
                data={adjustments}
                dataItemKey={dataItemKey}
                gridColumns={gridColumns}
                isReadOnly={isReadOnly}
                onSelectedItemsChange={onSelectedItemsChange} 
                selectMode={selectMode ?? 'single'}
                selectedItems={selectedDetails ?? undefined}
                selectionGroupField={selectionGroupField}
            />
            {showTotal && adjustments.length > 1 && (
                <div className="total-row">
                    <div className={'total-content'}>
                        <div>Total</div>
                        <div className={totalValue < 0 ? 'negative' : 'positive'}>
                        {getTotalAdjustments}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
